import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from './ui/form';
import { DialogFooter } from './ui/dialog';
import { toast } from './ui/use-toast';
import { Checkbox } from './ui/checkbox';
import { Loader2, Plus, Edit, Trash2, Search, X, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { getBaseUrl } from '@/lib/api';

// UQC Options (unchanged)
const UQC_OPTIONS = [
  'BAGS', 'BALE', 'BUNDLES', 'BUCKLES', 'BILLION OF UNITS', 'BOX', 'BOTTLES', 'BUNCHES', 
  'CANS', 'CUBIC METERS', 'CUBIC CENTIMETER', 'CENTIMETERS', 'CARTONS', 'DOZENS', 'DRUMS', 
  'GREAT GROSS', 'GRAMMES', 'GROSS', 'GROSS YARDS', 'KILOGRAMS', 'KILOLITRE', 'MILILITRE', 
  'METERS', 'METRIC TON', 'NUMBERS', 'PACKS', 'PIECES', 'PAIRS', 'QUINTAL', 'ROLLS', 
  'SETS', 'SQUARE FEET', 'SQUARE METERS', 'SQUARE YARDS', 'TABLETS', 'TEN GROSS', 
  'THOUSANDS', 'TONNES', 'TUBES', 'US GALLONS', 'UNITS', 'YARDS', 'OTHERS'
];

// Product form schema - base schema without LocationID
const baseProductSchema = z.object({
  Title: z.string().min(1, "Product name is required"),
  SgstPer: z.coerce.number().min(0, "SGST% must be 0 or greater").default(0),
  CGstPer: z.coerce.number().min(0, "CGST% must be 0 or greater").default(0),
  IgstPer: z.coerce.number().min(0, "IGST% must be 0 or greater").default(0),
  CessPer: z.coerce.number().min(0, "Cess% must be 0 or greater").default(0),
  MRP: z.coerce.number().min(0, "SP Inc GST must be 0 or greater").default(0),
  SPExclGST: z.coerce.number().min(0, "SP Excl GST must be 0 or greater").default(0),
  CostPrice: z.coerce.number().min(0, "Cost Price must be 0 or greater").default(0),
  NetQty: z.coerce.number().min(0, "Basic Price must be 0 or greater").default(0),
  UQCName: z.string().min(1, "UQC is required"),
  Category: z.string().min(1, "Category is required"),
  Barcode: z.string().min(1, "Barcode is required"),
  DiscountPer: z.coerce.number().min(0, "Discount% must be 0 or greater").default(0),
  Discount: z.coerce.number().min(0, "Discount must be 0 or greater").default(0),
  HSN: z.string().min(1, "HSN/SAC is required").default("1000"),
  BrandName: z.string().optional(),
  Size: z.string().optional(),
  Articlecode: z.string().optional(),
  Color: z.string().optional(),
  Style: z.string().optional(),
  Stylecode: z.string().optional(),
  Extra: z.string().optional(),
  Available: z.coerce.number().default(0),
  MinStQty: z.coerce.number().min(0, "ROL must be 0 or greater").default(0),
  imgStr: z.string().optional().or(z.literal("")),
  isService: z.boolean().default(false),
  hasSerial: z.boolean().default(false),
  isTraceable: z.boolean().default(false),
  Gender: z.enum(["", "male", "female", "other"]).optional(),
  PMargin: z.coerce.number().min(0, "Profit Margin must be 0 or greater").default(0),
  Rack: z.string().optional(),
  Shelf: z.string().optional(),
});

// Add LocationID only for add mode
const productSchema = baseProductSchema.extend({
  LocationID: z.preprocess(
    (val) => (val === '' || val === undefined || val === null ? 0 : Number(val)),
    z.number().refine((val) => val > 0, { message: 'Location is required' })
  ),
});

type ProductFormValues = z.infer<typeof productSchema> & {
  LocationID?: number;
};

interface ProductEntryFormProps {
  token: string;
  onSuccess: (productData?: any) => void;
  onCancel: () => void;
  showFooter?: boolean;
  mode?: 'add' | 'edit';
  initialValues?: Partial<ProductFormValues> & { Code?: number };
  /** When false, hide the "Service" checkbox. Default true. */
  showServiceCheckbox?: boolean;
  /** When false, hide the "Trackable Inventory" checkbox. Default true. */
  showTrackableCheckbox?: boolean;
  /** When false, hide the "Gender" field and dropdown. Default false so it is hidden unless setting is 1. */
  showGenderField?: boolean;
}

interface Category {
  id: number;
  Category: string;
}

interface Location {
  ID: number;
  Name: string;
}

const ProductEntryForm = forwardRef<ProductEntryFormHandle, ProductEntryFormProps>(({ token, onSuccess, onCancel, showFooter, mode = 'add', initialValues, showServiceCheckbox = true, showTrackableCheckbox = true, showGenderField = false }, ref) => {
  const navigate = useNavigate();
  const location = useLocation() as any;
  const returnState = (location && (location as any).state) || undefined as any;
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Category management state
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isCategorySubmitting, setIsCategorySubmitting] = useState(false);
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<number | null>(null);

  const baseUrl = getBaseUrl();
  const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

  const form = useForm<ProductFormValues>({
    resolver: zodResolver(mode === 'edit' ? baseProductSchema : productSchema),
    defaultValues: initialValues || {
      Title: '',
      SgstPer: 0,
      CGstPer: 0,
      IgstPer: 0,
      CessPer: 0,
      MRP: 0,
      SPExclGST: 0,
      CostPrice: 0,
      NetQty: 0,
      UQCName: 'PIECES',
      Category: '',
      Barcode: '',
      DiscountPer: 0,
      Discount: 0,
      HSN: '1000',
      BrandName: '',
      Size: '',
      Articlecode: '',
      Color: '',
      Style: '',
      Stylecode: '',
      Extra: '',
      Available: 0,
      MinStQty: 0,
      imgStr: '',
      isService: false,
      hasSerial: false,
      isTraceable: false,
      Gender: '',
      PMargin: 0,
      Rack: '',
      Shelf: '',
    },
  });

  const formRef = useRef<HTMLFormElement>(null);
  const lastChangedField = useRef<'MRP' | 'SPExclGST' | null>(null);

  useImperativeHandle(ref, () => ({
    submit: () => {
      console.log('Submitting form via ref');
      if (formRef.current) {
        formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      } else {
        console.error('Form reference is not available');
      }
    }
  }));

  // Normalize initial checkbox state per latest rules
  useEffect(() => {
    const service = form.getValues('isService');
    if (service) {
      if (form.getValues('hasSerial')) form.setValue('hasSerial', false);
      if (form.getValues('isTraceable')) form.setValue('isTraceable', false);
    }
  }, []);

  // Determine destination after add/update/cancel
  const getReturnDestination = () => {
    // If showFooter is false, we're in a modal context, so don't navigate
    if (!showFooter) {
      return {
        path: 'modal',
        state: {
          scrollToSection: 'products',
          productAdded: true,
        } as any,
      };
    }
    
    if (returnState?.returnTo) {
      if (returnState.returnTo === 'back') {
        return {
          path: 'back',
          state: {
            scrollToSection: returnState.returnSection || 'products',
            productAdded: true,
          } as any,
        };
      }
      return {
        path: returnState.returnTo,
        state: {
          scrollToSection: returnState.returnSection || 'products',
          productAdded: true,
        } as any,
      };
    }
    return { path: '/', state: { scrollToSection: 'products' } as any };
  };

  // Fetch categories and locations
  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers: Record<string, string> = {};
        if (authToken && authToken !== "Bearer ") {
          headers['Authorization'] = authToken;
        }
        const categoriesRes = await fetch(`${baseUrl}/api/Categories`, { headers });
        if (categoriesRes.ok) {
          const categoriesData = await categoriesRes.json();
          setCategories(categoriesData.data || []);
        } else {
          console.error('Failed to fetch categories:', categoriesRes.status, categoriesRes.statusText);
        }

        const locationsRes = await fetch(`${baseUrl}/api/Locations`, {
          headers: { 'Authorization': authToken }
        });
        if (locationsRes.ok) {
          const locationsData = await locationsRes.json();
          setLocations(locationsData.data || []);
        } else {
          console.error('Failed to fetch locations:', locationsRes.status, locationsRes.statusText);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast({
          title: 'Error',
          description: 'Failed to fetch categories or locations',
          variant: 'destructive'
        });
      }
    };

    fetchData();
  }, [baseUrl, authToken]);

  // Category management functions
  const fetchCategories = async () => {
    try {
      const headers: Record<string, string> = {};
      if (authToken && authToken !== "Bearer ") {
        headers['Authorization'] = authToken;
      }
      const response = await fetch(`${baseUrl}/api/Categories`, { headers });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.data || []);
      } else {
        console.error('Failed to fetch categories:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch categories',
        variant: 'destructive'
      });
    }
  };

  const addCategory = async () => {
    if (!newCategoryName.trim()) {
      toast({
        title: 'Error',
        description: 'Category name is required',
        variant: 'destructive'
      });
      return;
    }

    setIsCategorySubmitting(true);
    try {
      const response = await fetch(`${baseUrl}/api/Categories`, {
        method: 'POST',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          CollectionName: newCategoryName.trim()
        })
      });

      const result = await response.json();
      if (result.status === 0) {
        toast({
          title: 'Success',
          description: 'Category added successfully'
        });
        setNewCategoryName('');
        await fetchCategories();
        setShowCategoryDialog(false);
      } else {
        toast({
          title: 'Error',
          description: result.message || 'Failed to add category',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error adding category:', error);
      toast({
        title: 'Error',
        description: 'Failed to add category',
        variant: 'destructive'
      });
    } finally {
      setIsCategorySubmitting(false);
    }
  };

  const updateCategory = async () => {
    if (!editingCategory || !newCategoryName.trim()) {
      toast({
        title: 'Error',
        description: 'Category name is required',
        variant: 'destructive'
      });
      return;
    }

    setIsCategorySubmitting(true);
    try {
      const response = await fetch(`${baseUrl}/api/Categories`, {
        method: 'PUT',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          Id: editingCategory.id,
          CollectionName: newCategoryName.trim()
        })
      });

      const result = await response.json();
      if (result.status === 0) {
        toast({
          title: 'Success',
          description: 'Category updated successfully'
        });
        setNewCategoryName('');
        setEditingCategory(null);
        await fetchCategories();
        setShowCategoryDialog(false);
      } else {
        toast({
          title: 'Error',
          description: result.message || 'Failed to update category',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error updating category:', error);
      toast({
        title: 'Error',
        description: 'Failed to update category',
        variant: 'destructive'
      });
    } finally {
      setIsCategorySubmitting(false);
    }
  };

  const deleteCategory = async (categoryId: number) => {
    // Show custom delete confirmation dialog
    setCategoryToDelete(categoryId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!categoryToDelete) return;

    try {
      const response = await fetch(`${baseUrl}/api/Categories`, {
        method: 'DELETE',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          Id: categoryToDelete
        })
      });

      const result = await response.json();
      if (result.status === 0) {
        toast({
          title: 'Success',
          description: 'Category deleted successfully',
          className: 'bg-green-600 text-white border-green-700'
        });
        await fetchCategories();
      } else {
        toast({
          title: 'Cannot Delete Category',
          description: result.message || 'Failed to delete category',
          variant: 'destructive',
          className: 'bg-red-600 text-white border-red-700'
        });
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete category',
        variant: 'destructive'
      });
    } finally {
      setShowDeleteConfirm(false);
      setCategoryToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setCategoryToDelete(null);
  };

  const openCategoryDialog = () => {
    setShowCategoryDialog(true);
    setCategorySearchQuery('');
    setEditingCategory(null);
    setNewCategoryName('');
  };

  const startEditCategory = (category: Category) => {
    setEditingCategory(category);
    setNewCategoryName(category.Category);
  };

  const cancelEditCategory = () => {
    setEditingCategory(null);
    setNewCategoryName('');
  };

  // Filter categories based on search query
  const filteredCategories = categories.filter(category =>
    category.Category.toLowerCase().includes(categorySearchQuery.toLowerCase())
  );

  // Watch form values for calculations (unchanged)
  const watchedValues = form.watch();
  const { SgstPer, CGstPer, CessPer, MRP, SPExclGST, DiscountPer, Discount } = watchedValues;

  // Auto-calculate IGST% (unchanged)
  useEffect(() => {
    const sgstValue = parseFloat(SgstPer?.toString() || '0');
    const cgstValue = parseFloat(CGstPer?.toString() || '0');
    const igstValue = sgstValue + cgstValue;
    form.setValue('IgstPer', igstValue);
  }, [SgstPer, CGstPer, form]);

  const initialSpSyncDone = useRef(false);

  useEffect(() => {
    initialSpSyncDone.current = false;
  }, [mode, initialValues]);

  // Ensure edit form shows SPExclGST consistent with GST rules
  useEffect(() => {
    if (mode === 'edit' && !initialSpSyncDone.current) {
      const mrpValue = parseFloat(form.getValues('MRP')?.toString() || '0');
      const sgstValue = parseFloat(form.getValues('SgstPer')?.toString() || '0');
      const cgstValue = parseFloat(form.getValues('CGstPer')?.toString() || '0');
      const cessValue = parseFloat(form.getValues('CessPer')?.toString() || '0');
      // TotalTax% = GST% (SGST + CGST) + Cess%
      const totalTaxRate = (sgstValue + cgstValue + cessValue) / 100;

      if (mrpValue > 0) {
        // P_ex = P_inc / (1 + TotalTax% / 100)
        const spExclGST = mrpValue / (1 + totalTaxRate);
        form.setValue('SPExclGST', Number(spExclGST.toFixed(2)), { shouldDirty: false });
      } else {
        form.setValue('SPExclGST', 0, { shouldDirty: false });
      }
      initialSpSyncDone.current = true;
    }
  }, [mode, form, initialValues]);

  // Synchronized two-way calculation for MRP and SPExclGST
  useEffect(() => {
    const sgstValue = parseFloat(SgstPer?.toString() || '0');
    const cgstValue = parseFloat(CGstPer?.toString() || '0');
    const cessValue = parseFloat(CessPer?.toString() || '0');
    const totalTaxRate = (sgstValue + cgstValue + cessValue) / 100;

    // Whenever MRP or tax percentages change, refresh SPExclGST unless user is directly editing it
    if (lastChangedField.current !== 'SPExclGST') {
      lastChangedField.current = 'MRP';
      if (!MRP || Number(MRP) === 0) {
        form.setValue('SPExclGST', 0, { shouldValidate: true, shouldDirty: false });
      } else {
        // P_ex = P_inc / (1 + TotalTax% / 100)
        const spExclGST = Number(MRP) / (1 + totalTaxRate);
        form.setValue('SPExclGST', Number(spExclGST.toFixed(2)), { shouldValidate: true, shouldDirty: false });
      }
    }
  }, [MRP, SgstPer, CGstPer, CessPer, form]);

  useEffect(() => {
    const sgstValue = parseFloat(SgstPer?.toString() || '0');
    const cgstValue = parseFloat(CGstPer?.toString() || '0');
    const cessValue = parseFloat(CessPer?.toString() || '0');
    const totalTaxRate = (sgstValue + cgstValue + cessValue) / 100;

    // When SPExclGST is changed, update MRP (but only if SPExclGST was the last field changed)
    if (form.getFieldState('SPExclGST').isDirty && lastChangedField.current !== 'MRP') {
      lastChangedField.current = 'SPExclGST';
      if (!SPExclGST || Number(SPExclGST) === 0) {
        form.setValue('MRP', 0, { shouldValidate: true });
      } else {
        // P_inc = P_ex * (1 + TotalTax% / 100)
        const mrp = Number(SPExclGST) * (1 + totalTaxRate);
        form.setValue('MRP', Number(mrp.toFixed(2)), { shouldValidate: true });
      }
    }
  }, [SPExclGST, SgstPer, CGstPer, CessPer, form]);

  // Handle discount logic - Two-way binding between DiscountPer and Discount
  const lastDiscountField = useRef<'percent' | 'amount' | null>(null);

  useEffect(() => {
    const discountPer = parseFloat(DiscountPer?.toString() || '0');
    const mrpValue = parseFloat(MRP?.toString() || '0');
    
    if (discountPer > 0 && mrpValue > 0 && lastDiscountField.current !== 'amount') {
      // Calculate discount amount from percentage
      const discountAmount = (mrpValue * discountPer) / 100;
      form.setValue('Discount', Number(discountAmount.toFixed(2)), { shouldValidate: false });
      lastDiscountField.current = 'percent';
    } else if (discountPer === 0 && lastDiscountField.current === 'percent') {
      // Clear discount amount if percentage is cleared
      form.setValue('Discount', 0, { shouldValidate: false });
      lastDiscountField.current = null;
    }
  }, [DiscountPer, MRP, form]);

  useEffect(() => {
    const discount = parseFloat(Discount?.toString() || '0');
    const mrpValue = parseFloat(MRP?.toString() || '0');
    
    if (discount > 0 && mrpValue > 0 && lastDiscountField.current !== 'percent') {
      // Calculate discount percentage from amount
      const discountPer = (discount / mrpValue) * 100;
      form.setValue('DiscountPer', Number(discountPer.toFixed(2)), { shouldValidate: false });
      lastDiscountField.current = 'amount';
    } else if (discount === 0 && lastDiscountField.current === 'amount') {
      // Clear discount percentage if amount is cleared
      form.setValue('DiscountPer', 0, { shouldValidate: false });
      lastDiscountField.current = null;
    }
  }, [Discount, MRP, form]);

  // Generate barcode (unchanged)
  const generateBarcode = () => {
    const random = Math.floor(Math.random() * 90000) + 10000;
    form.setValue('Barcode', random.toString());
  };

  // Calculate Offer Price - Based ONLY on MRP and Discount/DiscountPer
  const calculateOfferPrice = () => {
    const discountPercent = parseFloat(DiscountPer?.toString() || '0');
    const discountAmount = parseFloat(Discount?.toString() || '0');

    // Ensure MRP is a valid number
    const mrpNum = typeof MRP === 'number' ? MRP : parseFloat(MRP) || 0;

    if (discountPercent > 0) {
      // Percent discount: subtract percentage from MRP
      const discountedPrice = mrpNum * (1 - discountPercent / 100);
      return Number(discountedPrice.toFixed(2));
    } else if (discountAmount > 0) {
      // Rupee discount: subtract amount directly from MRP
      return Number((mrpNum - discountAmount).toFixed(2));
    } else {
      // No discount
      return Number(mrpNum.toFixed(2));
    }
  };

  // Parse API error (unchanged)
  const parseApiError = (result: any): string => {
    if (typeof result.message === 'string' && result.message) {
        return result.message;
    }
    if (Array.isArray(result.errors) && result.errors.length > 0) {
        return result.errors.map((e: any) => e.msg).join(', ');
    }
    return `An unknown error occurred.`;
  };
  
  const testApiConnection = async () => {
    try {
        const response = await fetch(`${baseUrl}/api/Product`, {
            method: 'OPTIONS', // Use a lightweight method
            headers: { 'Authorization': authToken }
        });
        if (response.ok) {
            toast({ title: 'API Test', description: 'Connection successful.' });
        } else {
            toast({ title: 'API Test', description: `Connection failed: ${response.status}`, variant: 'destructive' });
        }
    } catch (error) {
        toast({ title: 'API Test', description: 'Connection failed.', variant: 'destructive' });
    }
  };

      const onSubmit = async (values: ProductFormValues) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    
    // Log the form values being submitted
    console.log(`[${mode.toUpperCase()}] Product Form Data:\n${JSON.stringify(values, null, 2)}`);
    form.clearErrors("Barcode");

    try {
      // Step 1: Separate quantity and location from the main product data.
      const { Available, LocationID, ...productData } = values;

      // Compute today's date in IST (Asia/Kolkata) in YYYY-MM-DD format
      const istNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
      const istYear = istNow.getFullYear();
      const istMonth = String(istNow.getMonth() + 1).padStart(2, '0');
      const istDay = String(istNow.getDate()).padStart(2, '0');
      const createdOnDateStr = `${istYear}-${istMonth}-${istDay}`;

      const payload = {
        ...productData,
        LocationID: String(LocationID ?? ''),
        HasSerialNo: productData.hasSerial ? 1 : 0,
        CreatedOn: createdOnDateStr,
      };
      
      Object.keys(payload).forEach(key => {
        const typedKey = key as keyof typeof payload;
        if (payload[typedKey] === '') {
            (payload as any)[typedKey] = null;
        }
      });
      
      // Step 2: Call POST /api/Product with the main data.
      console.log(`[${mode.toUpperCase()}] API Request Payload:\n${JSON.stringify(payload, null, 2)}`);
      
      const productResponse = await fetch(`${baseUrl}/api/Product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: authToken },
        body: JSON.stringify(payload),
      });

      const productResult = await productResponse.json();
      console.log('Add product result:', productResult);

      if (!productResponse.ok) {
        throw new Error(parseApiError(productResult) || `Failed to add product. Status: ${productResponse.status}`);
      }

      // Handle different status codes
      if (productResult.status === 0) {
        // Success - show success message
        toast({
          title: '✅ Success',
          description: 'Product Added Successfully',
          className: 'bg-green-50 border-green-200 text-green-800'
        });
      } else if (productResult.status === -1) {
        // Error - show the message from API response
        throw new Error(productResult.message || 'Failed to add product.');
      } else {
        // For any other status, treat as error
        throw new Error(parseApiError(productResult) || 'Failed to add product.');
      }
      
      const productCode = productResult.data?.[0]?.Code;

      // Step 3: If product creation is successful and we have a quantity, call POST /api/UpdateQty.
      if (productCode && Available && Available > 0 && LocationID) {
        toast({
          title: 'Product Created Successfully',
          description: 'Product details saved. Now updating stock...',
        });

        const qtyPayload = {
          ProductCode: productCode,
          LocationID: LocationID,
          Qty: Available,
        };

        // Log UpdateQty payload
        console.log('[UPDATEQTY] API Request Payload:\n' + JSON.stringify(qtyPayload, null, 2));

        const qtyResponse = await fetch(`${baseUrl}/api/UpdateQty`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: authToken },
          body: JSON.stringify(qtyPayload),
        });

        const qtyResult = await qtyResponse.json();
        console.log('Update quantity result:', qtyResult);

        if (!qtyResponse.ok) {
          throw new Error(parseApiError(qtyResult) || `Failed to update quantity. Status: ${qtyResponse.status}`);
        }
        
        const isQtySuccess = qtyResult.status === 0 || qtyResult.status === 1 || qtyResult.message?.toLowerCase() === 'success';
        if (!isQtySuccess) {
           throw new Error(parseApiError(qtyResult) || 'Failed to update product quantity.');
        }

        setSuccessMessage('Product added and quantity updated successfully.');
      } else {
        setSuccessMessage('Product added successfully.');
      }

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        // Create product data object to pass to onSuccess
        const createdProductData = {
          ...values,
          Code: productCode,
          ProdCode: productCode,
          Available: values.Available || 0,
          GST: (values.SgstPer || 0) + (values.CGstPer || 0),
          UnitPrice: values.SPExclGST || 0,
        };
        
        // Decide where to go next
        const destination = getReturnDestination();
        if (destination.path === 'modal') {
          // In modal context, just call onSuccess without navigation
          if (onSuccess) onSuccess(createdProductData);
        } else if (destination.path === 'back') {
          sessionStorage.setItem('productAdded', 'true');
          sessionStorage.setItem('returnSection', destination.state.scrollToSection || 'products');
          if (onSuccess) onSuccess(createdProductData);
          window.history.back();
        } else {
          if (onSuccess) onSuccess(createdProductData);
          navigate(destination.path, { state: destination.state });
        }
      }, 2000);

    } catch (error) {
      console.error(`Error adding product:`, error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.';
      
      if (errorMessage.toLowerCase().includes('barcode') && errorMessage.toLowerCase().includes('exists')) {
          form.setError('Barcode', {
              type: 'manual',
              message: errorMessage,
          });
          toast({
              title: 'Duplicate Barcode',
              description: 'This barcode is already in use. Please enter a unique one.',
              variant: 'destructive',
          });
      } else {
          toast({
              title: 'Error',
              description: errorMessage,
              variant: 'destructive',
          });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onUpdate = async (values: ProductFormValues) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    form.clearErrors("Barcode");

    try {
      const productData = {
        Code: initialValues?.Code,
        Title: values.Title.trim(),
        Barcode: values.Barcode.trim(),
        HSN: values.HSN || "1000",
        MRP: Number(values.MRP || 0),
        CostPrice: Number(values.CostPrice || 0),
        UQCName: values.UQCName || "PIECES",
        DiscountPer: values.DiscountPer?.toString() || "0",
        Discount: Number(values.Discount || 0),
        BrandName: values.BrandName || "",
        Color: values.Color || "",
        Style: values.Style || "",
        Stylecode: values.Stylecode || "",
        Size: values.Size || "",
        NetQty: values.NetQty?.toString() || "0",
        Extra: values.Extra || "",
        Articlecode: values.Articlecode || "",
        MinStQty: values.MinStQty?.toString() || "0",
        SgstPer: Number(values.SgstPer || 0),
        CGstPer: Number(values.CGstPer || 0),
        IgstPer: Number(values.IgstPer || 0),
        Available: Number(values.Available || 0),
        Sold: 0.0,
        isTraceable: !!values.isTraceable,
        isService: !!values.isService,
        HasSerialNo: values.hasSerial ? 1 : 0,
        CessPer: Number(values.CessPer || 0),
        Category: values.Category || "",
        Gender: values.Gender || "",
        PMargin: Number(values.PMargin || 0),
        Rack: values.Rack || "",
        Shelf: values.Shelf || "",
        // Add LocationID in the same format previously used for edit (usually location name)
        LocationID:
          (locations.find(l => (initialValues as any)?.LocationID && l.ID === (initialValues as any)?.LocationID)?.Name) ||
          (typeof (initialValues as any)?.LocationID === 'string' ? (initialValues as any)?.LocationID : ''),
        imgStr: values.imgStr || "",
        SPExclGST: Number(values.SPExclGST || 0),
      };

      // Log the exact payload being sent for update
      const updatePayload = [productData];
      console.log('[EDIT] API Request URL:', `${baseUrl}/api/Product`);
      console.log('[EDIT] API Request Method:', 'PUT');
      console.log('[EDIT] API Request Payload (JSON):', JSON.stringify(updatePayload, null, 2));

      const response = await fetch(`${baseUrl}/api/Product`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: authToken },
        body: JSON.stringify(updatePayload)
      });

      const result = await response.json();
      console.log('[EDIT] API Response (JSON):', JSON.stringify(result, null, 2));

      if (!response.ok) {
        throw new Error(parseApiError(result) || `HTTP error! status: ${response.status}`);
      }
      
      const isSuccess = result.status === 1 || (result.status === 0 && result.message?.toLowerCase() === 'success');
      if (!isSuccess) {
        throw new Error(parseApiError(result) || 'Unknown error during update');
      }

      setSuccessMessage('Product updated successfully!');
      setShowSuccess(true);
      toast({ title: '✅ Success', description: 'Product updated successfully', className: 'bg-green-50 border-green-200 text-green-800' });
      setTimeout(() => {
        setShowSuccess(false);
        const destination = getReturnDestination();
        if (destination.path === 'modal') {
          // In modal context, just call onSuccess without navigation
          onSuccess();
        } else if (destination.path === 'back') {
          sessionStorage.setItem('productAdded', 'true');
          sessionStorage.setItem('returnSection', destination.state.scrollToSection || 'products');
          onSuccess();
          window.history.back();
        } else {
          onSuccess();
          navigate(destination.path, { state: destination.state });
        }
      }, 2000);

    } catch (error) {
      console.error('Error updating product:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update product';

      if (errorMessage.toLowerCase().includes('barcode') && errorMessage.toLowerCase().includes('exists')) {
          form.setError('Barcode', {
              type: 'manual',
              message: errorMessage,
          });
          toast({
              title: 'Duplicate Barcode',
              description: 'This barcode is already in use. Please enter a unique one.',
              variant: 'destructive',
          });
      } else {
          toast({ 
              title: 'Error', 
              description: errorMessage, 
              variant: 'destructive' 
          });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    form.reset();
    setShowSuccess(false);
    setSuccessMessage('');
  };

  function formatDateDMY(dateStr?: string) {
    if (!dateStr) return 'N/A';
    const match = dateStr.match(/^(\d{4})[-/](\d{2})[-/](\d{2})$/);
    if (!match) return dateStr;
    return `${match[3]}-${match[2]}-${match[1]}`;
  }

  useEffect(() => {
    if (form.formState.isSubmitted && Object.keys(form.formState.errors).length > 0) {
      console.log('Form validation errors:', form.formState.errors);
    }
  }, [form.formState]);

  console.log('Form rendered, isSubmitting:', isSubmitting);
  
  return (
    <Form {...form}>
      <style>{`
        #product-form input,
        #product-form textarea,
        #product-form select {
          -webkit-user-select: text;
          -moz-user-select: text;
          -ms-user-select: text;
          user-select: text;
        }
        #product-form input::selection,
        #product-form textarea::selection {
          background-color: rgba(59, 130, 246, 0.3);
        }
      `}</style>
      <form
        ref={formRef}
        id="product-form"
        onSubmit={form.handleSubmit(mode === 'edit' ? onUpdate : onSubmit)}
        className="space-y-6 android-form-container keyboard-aware-container"
        onDragStart={(e) => {
          const target = e.target as HTMLElement;
          if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }
        }}
      >
        {showSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  {successMessage}
                </p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowSuccess(false);
                    setSuccessMessage('');
                  }}
                  className="inline-flex bg-green-50 rounded-md p-1.5 text-green-500 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-600"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="Title"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-red-600">Product Name (Title)</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Enter product name" 
                    {...field} 
                    onDragStart={(e) => e.preventDefault()}
                    draggable="false"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="SgstPer"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-red-600">SGST%</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.01" 
                    placeholder="0.00" 
                    className="no-spinner"
                    {...field} 
                    onWheel={(e) => e.currentTarget.blur()}
                    onDragStart={(e) => { e.preventDefault(); return false; }}
                    draggable="false"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="CGstPer"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-red-600">CGST%</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.01" 
                    placeholder="0.00" 
                    className="no-spinner"
                    {...field} 
                    onWheel={(e) => e.currentTarget.blur()}
                    onDragStart={(e) => { e.preventDefault(); return false; }}
                    draggable="false"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="IgstPer"
            render={({ field }) => (
              <FormItem>
                <FormLabel>IGST%</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.01" 
                    placeholder="0.00" 
                    {...field} 
                    readOnly 
                    className="no-spinner bg-gray-50"
                    onWheel={(e) => e.currentTarget.blur()}
                    onDragStart={(e) => { e.preventDefault(); return false; }}
                    draggable="false"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="CessPer"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-red-600">Cess%</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.01" 
                    placeholder="0.00" 
                    className="no-spinner"
                    {...field} 
                    onWheel={(e) => e.currentTarget.blur()}
                    onDragStart={(e) => { e.preventDefault(); return false; }}
                    draggable="false"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="MRP"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-red-600">SP Inc GST (MRP)</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.01" 
                    placeholder="0" 
                    className="no-spinner"
                    {...field}
                    onChange={(e) => {
                      lastChangedField.current = 'MRP';
                      field.onChange(e);
                    }}
                    onWheel={(e) => e.currentTarget.blur()}
                    onDragStart={(e) => { e.preventDefault(); return false; }}
                    draggable="false"
                  />
                </FormControl>
                <p className="text-xs text-gray-500 mt-1">
                  Price including GST. Can be entered directly or calculated from SP Excl GST.
                </p>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="SPExclGST"
            render={({ field }) => (
              <FormItem>
                <FormLabel>SP Excl GST</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.01" 
                    placeholder="0" 
                    className="no-spinner"
                    {...field}
                    onChange={(e) => {
                      lastChangedField.current = 'SPExclGST';
                      field.onChange(e);
                    }}
                    onWheel={(e) => e.currentTarget.blur()}
                    onDragStart={(e) => { e.preventDefault(); return false; }}
                    draggable="false"
                  />
                </FormControl>
                <p className="text-xs text-gray-500 mt-1">
                  Price excluding GST. Can be entered directly or calculated from MRP.
                </p>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="CostPrice"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Excl GST Cost Price</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.01" 
                    placeholder="0" 
                    className="no-spinner"
                    {...field} 
                    onWheel={(e) => e.currentTarget.blur()}
                    onDragStart={(e) => { e.preventDefault(); return false; }}
                    draggable="false"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="NetQty"
            render={({ field }) => (
              <FormItem>
              <FormLabel>Basic Price</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.01" 
                    placeholder="0" 
                    className="no-spinner"
                    {...field} 
                    onWheel={(e) => e.currentTarget.blur()}
                    onDragStart={(e) => { e.preventDefault(); return false; }}
                    draggable="false"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="UQCName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-red-600">UQC</FormLabel>
                <FormControl>
                  <select 
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                    {...field}
                    onDragStart={(e) => { e.preventDefault(); return false; }}
                    draggable="false"
                  >
                    {UQC_OPTIONS.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="Category"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel className="text-red-600">Category (Name)</FormLabel>
                  <button
                    type="button"
                    onClick={openCategoryDialog}
                    className="text-blue-600 hover:text-blue-800 text-sm underline"
                  >
                    New Category
                  </button>
                </div>
                <FormControl>
                  <Select
                    value={field.value || ''}
                    onValueChange={field.onChange}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      {/* Fixed Search Bar */}
                      <div className="sticky top-0 bg-white border-b border-gray-200 p-2 z-10">
                        <div className="relative">
                          <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                          <Input
                            placeholder="Search categories..."
                            value={categorySearchQuery}
                            onChange={(e) => setCategorySearchQuery(e.target.value)}
                            className="pl-8 pr-8"
                          />
                          {categorySearchQuery && (
                            <button
                              type="button"
                              className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 hover:text-gray-600"
                              onClick={() => setCategorySearchQuery('')}
                              aria-label="Clear search"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* Scrollable Category List */}
                      <div className="max-h-60 overflow-y-auto">
                        {filteredCategories.map(category => (
                          <SelectItem key={category.id} value={category.Category}>
                            {category.Category}
                          </SelectItem>
                        ))}
                        {filteredCategories.length === 0 && (
                          <div className="p-2 text-gray-500 text-sm">
                            No categories found
                          </div>
                        )}
                      </div>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="Barcode"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-red-600">Barcode</FormLabel>
                <FormControl>
                  <div className="flex gap-2">
                    <Input 
                      placeholder="Scan or enter barcode" 
                      {...field} 
                      onDragStart={(e) => { e.preventDefault(); return false; }}
                      draggable="false"
                    />
                    {mode !== 'edit' && (
                      <Button type="button" variant="outline" size="sm" onClick={generateBarcode}>Generate</Button>
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="DiscountPer"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Discount%</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.01" 
                    placeholder="0" 
                    className="no-spinner"
                    {...field}
                    onWheel={(e) => e.currentTarget.blur()}
                    onChange={(e) => {
                      lastDiscountField.current = 'percent';
                      field.onChange(e);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="Discount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Discount (in rupees)</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.01" 
                    placeholder="0" 
                    className="no-spinner"
                    {...field}
                    onWheel={(e) => e.currentTarget.blur()}
                    onChange={(e) => {
                      lastDiscountField.current = 'amount';
                      field.onChange(e);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="space-y-2">
            <Label>Offer Price</Label>
            <Input type="number" step="0.01" value={calculateOfferPrice().toFixed(2)} readOnly className="no-spinner bg-gray-50" onWheel={(e) => e.currentTarget.blur()} />
          </div>
          <FormField
            control={form.control}
            name="HSN"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-red-600">HSN/SAC</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="1000" 
                    {...field} 
                    onDragStart={(e) => { e.preventDefault(); return false; }}
                    draggable="false"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="BrandName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Brand</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Enter brand name" 
                    {...field} 
                    onDragStart={(e) => { e.preventDefault(); return false; }}
                    draggable="false"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="Size"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Size</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Enter size" 
                    {...field} 
                    onDragStart={(e) => { e.preventDefault(); return false; }}
                    draggable="false"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="Articlecode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Article Code</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Enter article code" 
                    {...field} 
                    onDragStart={(e) => { e.preventDefault(); return false; }}
                    draggable="false"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="Color"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Color</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Enter color" 
                    {...field} 
                    onDragStart={(e) => { e.preventDefault(); return false; }}
                    draggable="false"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="Style"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Shelf</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Enter shelf" 
                    {...field} 
                    onDragStart={(e) => { e.preventDefault(); return false; }}
                    draggable="false"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="Stylecode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Rack</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Enter rack" 
                    {...field} 
                    onDragStart={(e) => { e.preventDefault(); return false; }}
                    draggable="false"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {mode !== 'edit' && (
            <FormField
              control={form.control}
              name="Available"
              render={({ field }) => {
                const hasSerial = form.watch('hasSerial');
                return (
                  <FormItem>
                    <FormLabel className="text-red-600">OP</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="0" 
                        {...field} 
                        disabled={hasSerial}
                        className={hasSerial ? "no-spinner bg-gray-100 cursor-not-allowed" : "no-spinner"}
                        onWheel={(e) => e.currentTarget.blur()}
                        onDragStart={(e) => { e.preventDefault(); return false; }}
                        draggable="false"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          )}
          <FormField
            control={form.control}
            name="MinStQty"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-red-600">ROL</FormLabel>
                <FormControl>
                  <Input type="number" placeholder="0" className="no-spinner" onWheel={(e) => e.currentTarget.blur()} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {showGenderField && (
            <FormField
              control={form.control}
              name="Gender"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Gender</FormLabel>
                  <FormControl>
                    <select
                      {...field}
                      value={typeof field.value === 'string' ? field.value : ''}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="w-full p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onDragStart={(e) => { e.preventDefault(); return false; }}
                      draggable="false"
                    >
                      <option value="">Select gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Option</option>
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          {mode !== 'edit' && (
            <FormField
              control={form.control}
              name="LocationID"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-red-600">Location</FormLabel>
                  <FormControl>
                    <select 
                      className="w-full p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={field.value === 0 ? '' : field.value || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          field.onChange(0);
                        } else {
                          const numericValue = Number(value);
                          field.onChange(numericValue);
                        }
                      }}
                      onDragStart={(e) => { e.preventDefault(); return false; }}
                      draggable="false"
                    >
                      <option value="">Select location</option>
                      {locations.map(location => (
                        <option key={location.ID} value={location.ID}>{location.Name}</option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>

        <div className="space-y-4">
          {(() => {
            const isService = form.watch('isService');
            const hasSerial = form.watch('hasSerial');
            const isTraceable = form.watch('isTraceable');

            return (
              <>
                {showServiceCheckbox && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="isService"
                      checked={isService}
                      onCheckedChange={(checked) => {
                        const next = Boolean(checked);
                        form.setValue('isService', next);
                        if (next) {
                          // Service ON => both others must be OFF
                          if (form.getValues('hasSerial')) form.setValue('hasSerial', false);
                          if (form.getValues('isTraceable')) form.setValue('isTraceable', false);
                        }
                      }}
                    />
                    <Label htmlFor="isService">Service</Label>
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="hasSerial"
                    checked={hasSerial}
                    disabled={isService}
                    onCheckedChange={(checked) => {
                      const next = Boolean(checked);
                      form.setValue('hasSerial', next);
                      if (next) {
                        // Serial ON => Trackable must be ON; Service must be OFF
                        if (!form.getValues('isTraceable')) form.setValue('isTraceable', true);
                        if (form.getValues('isService')) form.setValue('isService', false);
                      }
                    }}
                  />
                  <Label htmlFor="hasSerial">Has Serial Nos.</Label>
                </div>

                {showTrackableCheckbox && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="isTraceable"
                      checked={isTraceable}
                      disabled={isService}
                      onCheckedChange={(checked) => {
                        const next = Boolean(checked);
                        form.setValue('isTraceable', next);
                        if (next && form.getValues('isService')) {
                          // Trackable ON => Service must be OFF
                          form.setValue('isService', false);
                        }
                      }}
                    />
                    <Label htmlFor="isTraceable">Trackable Inventory</Label>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {showFooter && mode !== 'edit' && (
          <DialogFooter>
            <Button type="button" variant="outline" onClick={testApiConnection}>Test API</Button>
            <Button type="button" variant="outline" onClick={handleClear}>Clear</Button>
            <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : 'Add'}
            </Button>
          </DialogFooter>
        )}
      </form>

      {/* Category Management Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              {editingCategory ? 'Edit Category' : 'Manage Categories'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Add/Edit Category Form */}
            <div className="space-y-2">
              <Label htmlFor="categoryName">
                {editingCategory ? 'Edit Category Name' : 'New Category Name'}
              </Label>
              <div className="flex gap-2">
                <Input
                  id="categoryName"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Enter category name"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      if (editingCategory) {
                        updateCategory();
                      } else {
                        addCategory();
                      }
                    }
                  }}
                />
                {editingCategory && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={cancelEditCategory}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={editingCategory ? updateCategory : addCategory}
                  disabled={isCategorySubmitting || !newCategoryName.trim()}
                  className="flex-1"
                >
                  {isCategorySubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {editingCategory ? 'Updating...' : 'Adding...'}
                    </>
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      {editingCategory ? 'Update' : 'Add'}
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Categories List */}
            <div className="space-y-2">
              <Label>Existing Categories</Label>
              <div className="max-h-60 overflow-y-auto border rounded-md">
                {categories.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    No categories found
                  </div>
                ) : (
                  <div className="divide-y">
                    {categories.map((category) => (
                      <div
                        key={category.id}
                        className="flex items-center justify-between p-3 hover:bg-gray-50"
                      >
                        <span className="flex-1">{category.Category}</span>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEditCategory(category)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteCategory(category.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md bg-red-600 text-white border-red-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              Delete Category
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-white">
              Are you sure you want to delete this category?
            </p>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={cancelDelete}
                className="flex-1 bg-white text-gray-700 hover:bg-gray-50 border-white"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                className="flex-1 bg-red-700 text-white hover:bg-red-800 border-red-700"
              >
                OK
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Form>
  );
});

export default ProductEntryForm;

export type ProductEntryFormHandle = {
  submit: () => void;
};